import React from 'react';
import { 
  Droplets, 
  ThermometerSun, 
  Wind, 
  Sun, 
  CloudRain,
  Activity,
  Gauge,
  TrendingUp
} from 'lucide-react';

interface SensorValue {
  value: number;
  unit: string;
  timestamp?: string;
  status?: string;
  quality?: number | string;
}

interface DynamicSensorCardProps {
  sensorName: string;
  sensorData: SensorValue;
}

const sensorConfig: Record<string, { icon: any; label: string; color: string; thresholds?: { min?: number; max?: number } }> = {
  soilMoisture: {
    icon: Droplets,
    label: 'Humidité du Sol',
    color: 'blue',
    thresholds: { min: 30, max: 70 }
  },
  temperature: {
    icon: ThermometerSun,
    label: 'Température',
    color: 'orange',
    thresholds: { min: 15, max: 35 }
  },
  humidity: {
    icon: Wind,
    label: 'Humidité Air',
    color: 'cyan',
    thresholds: { min: 40, max: 80 }
  },
  light: {
    icon: Sun,
    label: 'Luminosité',
    color: 'yellow'
  },
  waterLevel: {
    icon: CloudRain,
    label: 'Niveau d\'Eau',
    color: 'indigo'
  },
  rainwater: {
    icon: CloudRain,
    label: 'Pluie',
    color: 'sky'
  },
  soilHumidity: {
    icon: Droplets,
    label: 'Humidité Sol',
    color: 'teal',
    thresholds: { min: 30, max: 70 }
  }
};

const DynamicSensorCard: React.FC<DynamicSensorCardProps> = ({ sensorName, sensorData }) => {
  const config = sensorConfig[sensorName] || {
    icon: Activity,
    label: sensorName.replace(/([A-Z])/g, ' $1').trim(),
    color: 'gray'
  };

  const Icon = config.icon;

  const getStatusColor = () => {
    if (!config.thresholds) return 'green';
    
    const { value } = sensorData;
    const { min, max } = config.thresholds;
    
    if (min !== undefined && value < min) return 'red';
    if (max !== undefined && value > max) return 'red';
    if (min !== undefined && value < min * 1.2) return 'yellow';
    if (max !== undefined && value > max * 0.8) return 'yellow';
    
    return 'green';
  };

  const statusColor = getStatusColor();
  
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    cyan: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800',
    sky: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800',
    teal: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800',
    gray: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
  };

  const iconColorClasses = {
    blue: 'text-blue-600 dark:text-blue-400',
    orange: 'text-orange-600 dark:text-orange-400',
    cyan: 'text-cyan-600 dark:text-cyan-400',
    yellow: 'text-yellow-600 dark:text-yellow-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    sky: 'text-sky-600 dark:text-sky-400',
    teal: 'text-teal-600 dark:text-teal-400',
    gray: 'text-gray-600 dark:text-gray-400',
    green: 'text-green-600 dark:text-green-400',
    red: 'text-red-600 dark:text-red-400'
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[config.color as keyof typeof colorClasses]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Icon className={`h-5 w-5 ${iconColorClasses[config.color as keyof typeof iconColorClasses]}`} />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {config.label}
          </span>
        </div>
        {statusColor !== 'green' && (
          <span className={`h-2 w-2 rounded-full ${
            statusColor === 'red' ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
          }`} />
        )}
      </div>
      
      <div className="flex items-baseline space-x-1">
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {sensorData.value}
        </span>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {sensorData.unit}
        </span>
      </div>
      
      {sensorData.status && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 capitalize">
          Status: {sensorData.status}
        </div>
      )}
      
      {config.thresholds && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Optimal: {config.thresholds.min}-{config.thresholds.max} {sensorData.unit}
        </div>
      )}
    </div>
  );
};

export default DynamicSensorCard;
